import { useEffect, useState, useMemo } from 'react';
import { ChevronsUpDown, Building, Search, ChevronRight, ChevronDown, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/contexts/AuthContext';
import userStorage from '@/lib/userStorage';
import { cn } from '@/lib/utils';

interface Practice {
  id: number;
  tin: string;
  tin_name: string;
  practice_alias?: string;
}

interface Client {
  id: number;
  client_alias: string;
  account_id?: string;
  practices: Practice[];
}

export default function TopPagePracticeSelector() {
  const {
    clients,
    loading: authLoading,
    selectedClient,
    setSelectedClient,
    selectedPractice,
    setSelectedPractice,
  } = useAuth();

  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedClients, setExpandedClients] = useState<Set<number>>(new Set());

  // Reset search when popover closes
  useEffect(() => {
    if (!open) {
      setSearchQuery('');
    }
  }, [open]);

  // Helper to persist in session
  const persist = (cId?: number, pId?: number) => {
    if (cId) userStorage.setSessionItem('selectedClientId', cId.toString());
    if (pId) userStorage.setSessionItem('selectedPracticeId', pId.toString());
  };

  const handleSelect = (client: Client, practice: Practice) => {
    setSelectedClient(client);
    setSelectedPractice(practice);
    persist(client.id, practice.id);
    userStorage.setSessionItem('currentPractice', practice.id.toString());
    setOpen(false);
    
    // Navigate to dashboard if on home
    if (window.location.pathname === '/') {
      window.location.href = '/dashboard';
    }
  };

  const toggleClientExpand = (clientId: number) => {
    setExpandedClients(prev => {
      const next = new Set(prev);
      if (next.has(clientId)) {
        next.delete(clientId);
      } else {
        next.add(clientId);
      }
      return next;
    });
  };

  // Build flat list of all practices for search
  const allPractices = useMemo(() => {
    const practices: { practice: Practice; client: Client }[] = [];
    clients.forEach(client => {
      client.practices.forEach(practice => {
        practices.push({ practice, client });
      });
    });
    return practices;
  }, [clients]);

  // Filter practices based on search
  const filteredPractices = useMemo(() => {
    if (!searchQuery.trim()) return null; // null means show normal view
    
    const query = searchQuery.toLowerCase().trim();
    return allPractices.filter(({ practice, client }) =>
      practice.practice_alias?.toLowerCase().includes(query) ||
      practice.tin_name?.toLowerCase().includes(query) ||
      practice.tin?.includes(query) ||
      client.client_alias?.toLowerCase().includes(query)
    );
  }, [allPractices, searchQuery]);

  // Count total practices
  const totalPractices = allPractices.length;
  const totalClients = clients.length;

  /* ---------------------- loading ---------------------- */
  if (authLoading) {
    return (
      <Button variant="outline" disabled className="w-full md:w-auto md:min-w-[12rem] md:max-w-[16rem] justify-between">
        Loading…
      </Button>
    );
  }

  /* ---------------------- render ---------------------- */
  const buttonLabel = selectedPractice 
    ? (selectedPractice.practice_alias || selectedPractice.tin_name)
    : 'Select Practice';

  return (
    <div className="flex items-center gap-2">
      {selectedClient && (
        <span className="text-sm font-medium text-muted-foreground hidden 2xl:inline">
          {selectedClient.client_alias}:
        </span>
      )}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button 
            variant="outline" 
            className="w-full md:w-auto md:min-w-[12rem] md:max-w-[16rem] justify-between"
            role="combobox"
            aria-expanded={open}
          >
            <span className="truncate">{buttonLabel}</span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[340px] p-0" align="start">
          {/* Search Input */}
          <div className="p-3 border-b">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search practices..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
            <div className="text-xs text-muted-foreground mt-2">
              {filteredPractices 
                ? `${filteredPractices.length} matches`
                : `${totalPractices} practices · ${totalClients} clients`
              }
            </div>
          </div>

          {/* Scrollable Content */}
          <div className="max-h-[400px] overflow-y-auto">
            {/* Search Results */}
            {filteredPractices ? (
              <div className="p-2">
                {filteredPractices.length > 0 ? (
                  filteredPractices.map(({ practice, client }) => (
                    <button
                      key={practice.id}
                      onClick={() => handleSelect(client, practice)}
                      className={cn(
                        "w-full text-left px-3 py-2 rounded-md hover:bg-muted transition-colors flex items-center justify-between",
                        selectedPractice?.id === practice.id && "bg-primary/10"
                      )}
                    >
                      <div>
                        <div className="text-xs text-muted-foreground mb-0.5">
                          {client.client_alias}
                        </div>
                        <div className="font-medium text-sm">
                          {practice.practice_alias || practice.tin_name}
                        </div>
                      </div>
                      {selectedPractice?.id === practice.id && (
                        <Check className="h-4 w-4 text-primary" />
                      )}
                    </button>
                  ))
                ) : (
                  <div className="text-center py-6 text-muted-foreground">
                    <Search className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">No practices match "{searchQuery}"</p>
                    <button 
                      onClick={() => setSearchQuery('')}
                      className="text-primary text-xs mt-1 hover:underline"
                    >
                      Clear search
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <>
                {/* All Clients */}
                <div className="px-3 pb-2">
                  {clients.map((client, index) => {
                    const hasMultiplePractices = client.practices.length > 1;
                    const isExpanded = expandedClients.has(client.id);
                    const isLast = index === clients.length - 1;

                    return (
                      <div key={client.id}>
                        {/* Client Header */}
                        {hasMultiplePractices ? (
                          <button
                            onClick={() => toggleClientExpand(client.id)}
                            className="w-full text-left py-2 flex items-center justify-between hover:opacity-70 transition-opacity"
                          >
                            <span className="font-medium text-sm">
                              {client.client_alias}
                              <span className="text-muted-foreground font-normal ml-1">
                                ({client.practices.length})
                              </span>
                            </span>
                            {isExpanded ? (
                              <ChevronDown className="h-4 w-4 text-muted-foreground" />
                            ) : (
                              <ChevronRight className="h-4 w-4 text-muted-foreground" />
                            )}
                          </button>
                        ) : (
                          <div className="py-2">
                            <span className="font-medium text-sm">
                              {client.client_alias}
                              <span className="text-muted-foreground font-normal ml-1">
                                ({client.practices.length})
                              </span>
                            </span>
                          </div>
                        )}

                        {/* Practices (always show if single, show if expanded for multiple) */}
                        {(!hasMultiplePractices || isExpanded) && (
                          <div className="mb-1">
                            {client.practices.map((practice) => (
                              <button
                                key={practice.id}
                                onClick={() => handleSelect(client, practice)}
                                className={cn(
                                  "w-full text-left pl-5 pr-2 py-1.5 rounded-md hover:bg-muted transition-colors flex items-center justify-between text-sm",
                                  selectedPractice?.id === practice.id && "bg-primary/10"
                                )}
                              >
                                <span className="flex items-center gap-2">
                                  <span className="text-muted-foreground">└</span>
                                  <span className={cn(
                                    selectedPractice?.id === practice.id 
                                      ? "font-medium" 
                                      : ""
                                  )}>
                                    {practice.practice_alias || practice.tin_name}
                                  </span>
                                </span>
                                {selectedPractice?.id === practice.id && (
                                  <Check className="h-4 w-4 text-primary" />
                                )}
                              </button>
                            ))}
                          </div>
                        )}

                        {/* Light divider between clients */}
                        {!isLast && <div className="border-b my-2" />}
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}


